import React, { FC } from 'react';
import { Text, StyleSheet, View, ScrollView, Image } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { Element, scale as initialScale, notchBottom } from '@components/frame';
import { isPhone, fileFormatNames, bytesToView, isWeb } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import { font as initialFont } from '@styles/fonts';
import Button from '@styles/buttons';

import { getDocumentById } from '@reducers/search';
import { generateImageURL } from '@reducers/blobStorage';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import { useManageInfoModal } from './hooks/useManageInfoModal';
import ProgressiveImage from '@v2/components/imageHandling/components/ProgressiveImage';
import { useGetImageUri } from '@v2/components/imageHandling/hooks/useGetImageUri';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface InfoModalProps {
  images: ImageDocumentStoreItem[];
  selectedIndex: number;
  C: ColorFunction;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  triggerInfoModal: () => void;
}

interface TitleProps extends Pick<InfoModalProps, 'scale' | 'font' | 'C'> {
  shareFile: () => Promise<void>;
  shareFileLoading: boolean;
  image: ImageDocumentStoreItem;
}

interface InfoModalHeaderProps extends Pick<InfoModalProps, 'scale' | 'font' | 'C' | 'triggerInfoModal'> {}

interface ListProps extends Pick<InfoModalProps, 'C' | 'font' | 'scale'>, Pick<TitleProps, 'image'> {
  infoData: { name: string; value: string; hide?: boolean }[];
}

const InfoModalHeader: FC<InfoModalHeaderProps> = ({ C, triggerInfoModal, scale = initialScale, font = initialFont }) => {
  return (
    <View
      style={{
        height: scale(50),
        width: '100%',
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: C('Hairline/Light/No trans'),
        alignContent: 'center',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Info')}</Text>
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              right: 4,
            },
          },
        }}
      >
        <TouchableElement onPress={triggerInfoModal}>
          {({ boxStyle, pressHoverColor }) => (
            <Text style={[boxStyle, font.Headline, { color: C(pressHoverColor('Link/Dark/Special')), padding: scale(10) }]}>{i18n.t('Done')}</Text>
          )}
        </TouchableElement>
      </Element>
    </View>
  );
};

const Title: FC<TitleProps> = ({ image, C, scale = initialScale, font = initialFont, shareFile, shareFileLoading }) => {
  return (
    <View
      style={{
        width: '100%',
        paddingTop: scale(14),
        paddingLeft: scale(15),
        paddingRight: scale(15),
      }}
    >
      <View style={{ width: '100%', borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: C('Hairline/Light/No trans') }}>
        <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), flexWrap: 'wrap' }]}>{image.title}</Text>
        <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), flexWrap: 'wrap', paddingTop: scale(1) }]}>
          {i18n.t(fileFormatNames[image.extensionOriginal])} - {bytesToView(image.fileSize)}
        </Text>
        <View style={{ width: scale(63), height: scale(26), marginTop: scale(13), marginBottom: scale(22) }}>
          <Button {...{ variant: 'S', type: 'Primary', onPress: shareFile, loading: shareFileLoading }}>{i18n.t('OPEN')}</Button>
        </View>
      </View>
    </View>
  );
};

const List: FC<ListProps> = ({ C, image, scale = initialScale, font = initialFont, infoData }) => {
  return (
    <View style={{ width: '100%', paddingTop: scale(16) }}>
      <View style={{ width: '100%' }}>
        <Text style={[font['Title 2'], { color: C('Label/Light/Primary'), paddingHorizontal: scale(15) }]}>{i18n.t('Information')}</Text>
        <View style={{ marginTop: scale(13) }}>
          {infoData.map((item, index) => {
            console.log('item', item);
            if (item.hide) return null;
            return (
              <View
                key={item.name}
                style={{
                  flexDirection: 'row',
                  borderBottomWidth: index === infoData.length - 1 ? 0 : StyleSheet.hairlineWidth,
                  borderBottomColor: C('Hairline/Light/No trans'),
                  justifyContent: 'space-between',
                  backgroundColor: C('Background/Light/Field'),
                  paddingHorizontal: scale(15),
                  height: scale(36),
                  alignItems: 'center',
                  gap: scale(30),
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text numberOfLines={1} style={[font[isPhone ? 'Footnote' : 'Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
                    {i18n.t(item.name)}
                  </Text>
                </View>
                <View style={{ flex: isPhone ? 2 : 3 }}>
                  <Text numberOfLines={1} style={[font[isPhone ? 'Caption 1' : 'Subhead 2'], { color: C('Label/Light/Primary') }]}>
                    {item.value}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
};

const Info: FC<InfoModalProps> = ({ images, selectedIndex, triggerInfoModal, C, font = initialFont, scale = initialScale }) => {
  const { infoData, shareFile, shareFileLoading, image } = useManageInfoModal({ images, selectedIndex });

  // const { getHighResUri, getLowResUri } = useGetImageUri();

  // if (!imageLoaded) {
  //   return (
  //     <View style={{ minHeight: scale(200) }}>
  //       <Loading />
  //     </View>
  //   );
  // }

  return (
    <View testID={TEST_IDS.documentsInfoModal.root} style={[isWeb && { flex: 1 }]}>
      <InfoModalHeader C={C} triggerInfoModal={triggerInfoModal} font={font} scale={scale} />
      <ScrollView contentContainerStyle={[!isWeb && { paddingBottom: scale(60) + notchBottom }]}>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Image source={{ uri: image?.urlSmall }} style={{ width: scale(300), height: scale(200) }} resizeMode="contain" />
        </View>
        <Title image={image} C={C} font={font} scale={scale} shareFile={shareFile} shareFileLoading={shareFileLoading} />
        <List image={image} C={C} font={font} scale={scale} infoData={infoData} />
      </ScrollView>
    </View>
  );
};

export default connect(null, { getDocumentById, generateImageURL })(Info);
